import { Box, Button, Icon, Tooltip, Typography, useMediaQueryContext } from "@drone-js/core";
import { useUrlBuilder } from "apps/commons/hooks/useUrlBuilder";
import type { FunctionComponent } from "react";
import { BoldTextParser } from "./BoldTextParser";
import { usePartnerMarketingModelValue } from "./partnerMarketingModelAtom";
import { useSetPassedPropertyWizardSection } from "./passedPropertyWizardSectionAtom";
import { useIntersectionObserver } from "./useIntersectionObserver";

interface HeroSectionSubtitle {
    text: string;
}

interface HeroSectionValues {
    text: string;
    tooltipText?: string;
}

interface HeroSectionSubtitleProps {
    subtitles: HeroSectionSubtitle[];
}

const HeroSectionSubtitle: FunctionComponent<HeroSectionSubtitleProps> = ({ subtitles }) => {
    const mediaQuery = useMediaQueryContext();
    return (
        <Box display="flex" flexDirection="row" gap="8" alignItems="center" textColor="base.subtle">
            {subtitles?.map((subtitle, index) => (
                <Box key={subtitle?.text} display="flex" gap="8" alignItems="center">
                    <Typography variant={mediaQuery.m ? "body-default-strong" : "body-s-strong"}>
                        {subtitle?.text}
                    </Typography>
                    {index !== subtitles?.length - 1 && (
                        <Box width="4px" height="4px" borderWidth="2" borderStyle="solid" borderRadius="max" mt="4" />
                    )}
                </Box>
            ))}
        </Box>
    );
};

interface HeroSectionValueListProps {
    values: HeroSectionValues[];
}

const HeroSectionValueList: FunctionComponent<HeroSectionValueListProps> = ({ values }) => {
    const setPassedPropertyWizardSection = useSetPassedPropertyWizardSection();
    const { componentRef: listPropertyButtonRef } = useIntersectionObserver(setPassedPropertyWizardSection);

    return (
        <Box display="flex" alignItems="center" width="100%" gap="8" flexDirection="column">
            {values?.map((value, index) => (
                <Box
                    key={value?.text}
                    display="flex"
                    flexDirection="row"
                    gap="8"
                    ref={index === values?.length - 1 ? listPropertyButtonRef : undefined}
                    alignItems="center"
                    maxWidth="100%"
                >
                    <Icon id="fill.symbol.check" size="20" color="feedback.positive" />
                    <BoldTextParser text={value?.text} />
                    {value.tooltipText && (
                        <Tooltip
                            content={value.tooltipText}
                            textAlign="start"
                            pointerPosition="below"
                            {...(index === 1
                                ? {
                                      "data-element-name": "first-booking",
                                  }
                                : {})}
                        >
                            <Box display="flex" alignItems="center" pt="2">
                                <Icon id="line.symbol.circle-info" size="16" />
                            </Box>
                        </Tooltip>
                    )}
                </Box>
            ))}
        </Box>
    );
};

export const HeroContent: FunctionComponent = () => {
    const {
        heroSectionViewModel: { subtitles, values, listAnyPropertyOnAgodaText, listMyPropertyText },
    } = usePartnerMarketingModelValue();
    const urlBuilder = useUrlBuilder();

    return (
        <Box width="100%" pt="64" px="16" display="flex" justifyContent="center" zIndex={2} position="relative">
            <Box pt="32" width="100%">
                <Box display="flex" alignItems="center" width="100%" gap="32" flexDirection="column">
                    <Box display="flex" alignItems="center" width="100%" gap="16" flexDirection="column">
                        {subtitles?.length > 0 && <HeroSectionSubtitle subtitles={subtitles} />}
                        <Typography variant="heading-3xl" textAlign="center">
                            {listAnyPropertyOnAgodaText}
                        </Typography>
                    </Box>
                    {values?.length > 0 && <HeroSectionValueList values={values} />}
                    <Button
                        hierarchy="primary"
                        variant="label"
                        data-element-name="list-your-property"
                        label={listMyPropertyText}
                        href={urlBuilder.onboardingUrl()}
                    />
                </Box>
            </Box>
        </Box>
    );
};
